import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";

import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceTeleopCustomStreamConfiguration } from "@formant/core/model/IDeviceTeleopCustomStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { IValidation } from "@formant/core/model/IValidation";
import { rosTypeVideoFrame } from "@formant/core/model/rosTypes";
import { videoQualities } from "@formant/core/model/videoQualities";
import { VideoQuality } from "@formant/core/model/VideoQuality";
import { InputElement } from "@formant/ui/component/common/Field";
import { HStack } from "@formant/ui/component/common/HStack";
import { TextField } from "@formant/ui/component/common/TextField";

import { Help } from "../common/Help";
import { NumberField } from "../common/NumberField";
import { SelectField } from "../common/SelectField";
import { Switch } from "../common/Switch";
import { EditTeleopStreamPopup } from "./EditTeleopStreamPopup";

export interface ITeleopCompressedImageConfigurationProps {
    visible: boolean;
    streamName?: string;
    document: IDeviceConfigurationDocument;
    realtimeStreamNames?: string[];
    realtimeMode?: boolean;
    onChange: (document: IDeviceConfigurationDocument) => void;
    onClose: () => void;
}

interface IErrorState {
    errorMessage: string;
}

export class TeleopCompressedImageConfiguration extends Component<ITeleopCompressedImageConfigurationProps> {
    @observable
    private validation: IValidation | undefined;

    private label?: string;
    private initialLabel?: string;

    // eslint-disable-next-line react/state-in-constructor
    state: IErrorState = {
        errorMessage: "",
    };

    public render() {
        const { visible, streamName, document, realtimeMode } = this.props;
        const { errorMessage } = this.state;
        const rosStream = (
            realtimeMode ? document.realtime : document.teleop
        )?.rosStreams?.find(_ => _.topicName === streamName);
        const customStream = (
            realtimeMode ? document.realtime : document.teleop
        )?.customStreams?.find(_ => _.name === streamName);
        const stream = rosStream || customStream;
        if (stream?.label) {
            this.label = stream.label;
            if (!this.initialLabel) {
                this.initialLabel = stream.label;
            }
        }
        const qualityOnly =
            rosStream && rosStream.topicType === rosTypeVideoFrame;

        const clockSwitch = (
            <Switch
                state={stream?.overlayClock ?? false}
                onChange={this.onToggleOverlayClock(stream)}
                label="Clock Overlay"
                stateLabels={["No", "Yes"]}
                info="Overlay the video stream with a clock."
            />
        );

        return (
            <EditTeleopStreamPopup
                visible={visible}
                onClose={this.handleClose}
                title={`Configuration for: ${streamName}`}
            >
                {realtimeMode && (
                    <>
                        <TextField
                            value={stream?.label || ""}
                            onChange={this.onChangeLabel(stream)}
                            label="Connection"
                            placeholder="Connection"
                            autoFocus={true}
                            small={true}
                        />
                        {errorMessage && (
                            <div style={{ color: "red" }}>{errorMessage}</div>
                        )}
                    </>
                )}
                {!qualityOnly && (
                    <Switch
                        state={stream?.encodeVideo || false}
                        label="Enable encoding"
                        onChange={this.onChangeEncodeVideo(stream)}
                        stateLabels={["no", "yes"]}
                    />
                )}
                <Help
                    text="If YES, image stream will be sent through H264 encoder. If OFF, image stream will be passed directly to front end. If sending pre-encoded video, set this to YES. Click for docs."
                    to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                />
                {(stream?.encodeVideo || qualityOnly) && (
                    <>
                        <HStack
                            innerSpacing="sm"
                            hAlign="start"
                            vAlign="center"
                        >
                            <div>Video quality</div>
                            <Help
                                text="Preferred video quality. If this video source is also configured for telemetry, the lower of the two qualities will be used."
                                to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                            />
                        </HStack>
                        <SelectField
                            label="Preferred video quality"
                            value={stream?.quality}
                            onChange={this.onChangeQuality(stream)}
                            small={true}
                        >
                            {videoQualities.map(_ => (
                                <option key={_} value={_}>
                                    {_}
                                </option>
                            ))}
                        </SelectField>
                        <HStack vAlign="center" hAlign="space-between">
                            <div>Advanced Settings</div>
                            <Help
                                text="If you also configure your video settings via environment variables, the environment variables will override what you configure here. Click for docs."
                                to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                            />
                        </HStack>
                        <HStack
                            vAlign="center"
                            hAlign="start"
                            innerSpacing="sm"
                        >
                            <NumberField
                                label="Bitrate"
                                numberValue={stream?.bitrate}
                                allowUndefined={true}
                                invalid={!!this.validation?.bitrate}
                                onChangeNumber={this.onChangeBitrate(stream)}
                            />
                            <Help
                                text="Set your desired bitrate in kbps. If left blank, the agent will choose the best bitrate for the preferred quality selected above. If specified, the agent will be forced to use this bitrate. Click for docs."
                                to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                            />
                        </HStack>
                        <Switch
                            state={stream?.disableAdaptiveQuality ?? false}
                            onChange={this.onToggleDisableAdaptiveQuality(
                                stream,
                            )}
                            label="Disable Adaptive Quality"
                            stateLabels={["No", "Yes"]}
                            info="If set to No, the Formant agent will be able to reduce video quality in poor network connection to maintain video stream. If set to Yes, the agent will not be allowed to change the video quality."
                        />
                    </>
                )}
                {clockSwitch}
            </EditTeleopStreamPopup>
        );
    }

    private handleClose = () => {
        const { onClose } = this.props;

        if (this.state.errorMessage !== "") {
            return;
        }

        this.initialLabel = undefined;

        onClose();
    };

    private onChangeBitrate =
        (
            stream:
                | IDeviceTeleopRosStreamConfiguration
                | IDeviceTeleopCustomStreamConfiguration
                | undefined,
        ) =>
        (bitrate: number | undefined) => {
            if (!stream) {
                return;
            }
            if (bitrate !== undefined && bitrate < 1) {
                this.validation = {
                    bitrate: ["Bitrate must be positive"],
                };
                return;
            } else {
                this.validation = undefined;
            }
            const { document, onChange } = this.props;
            stream.bitrate = bitrate;
            onChange({ ...document });
        };
    private onToggleDisableAdaptiveQuality =
        (
            stream:
                | IDeviceTeleopRosStreamConfiguration
                | IDeviceTeleopCustomStreamConfiguration
                | undefined,
        ) =>
        (disableAdaptiveQuality: boolean) => {
            if (!stream) {
                return;
            }
            const { document, onChange } = this.props;
            stream.disableAdaptiveQuality = disableAdaptiveQuality;
            onChange({ ...document });
        };
    private onToggleOverlayClock =
        (
            stream:
                | IDeviceTeleopRosStreamConfiguration
                | IDeviceTeleopCustomStreamConfiguration
                | undefined,
        ) =>
        (overlayClock: boolean) => {
            if (!stream) {
                return;
            }
            const { document, onChange } = this.props;
            stream.overlayClock = overlayClock;
            onChange({ ...document });
        };
    private onChangeLabel =
        (
            stream:
                | IDeviceTeleopRosStreamConfiguration
                | IDeviceTeleopCustomStreamConfiguration
                | undefined,
        ) =>
        (event: ChangeEvent<InputElement>) => {
            if (!stream) {
                return;
            }
            const { realtimeMode, document, onChange } = this.props;
            this.label = event.target.value;
            stream.label = event.target.value;

            if (!this.label && realtimeMode) {
                this.setState({ errorMessage: "Name cannot be empty" });
                return;
            }

            if (
                this.label &&
                realtimeMode &&
                this.props.realtimeStreamNames?.includes(this.label) &&
                this.label !== this.initialLabel
            ) {
                this.setState({ errorMessage: "Name already in use" });
                return;
            }

            this.setState({ errorMessage: "" });

            onChange({ ...document });
        };
    private onChangeEncodeVideo =
        (
            stream:
                | IDeviceTeleopRosStreamConfiguration
                | IDeviceTeleopCustomStreamConfiguration
                | undefined,
        ) =>
        (encodeVideo: boolean) => {
            if (!stream) {
                return;
            }
            const { document, onChange } = this.props;
            stream.encodeVideo = encodeVideo;
            stream.quality = "720p";
            onChange({ ...document });
        };
    private onChangeQuality =
        (
            stream:
                | IDeviceTeleopRosStreamConfiguration
                | IDeviceTeleopCustomStreamConfiguration
                | undefined,
        ) =>
        (event: ChangeEvent<HTMLSelectElement>) => {
            if (!stream) {
                return;
            }
            const { document, onChange } = this.props;
            stream.quality = event.target.value as VideoQuality;
            onChange({ ...document });
        };
}
